import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import IdentityVerificationModal from '../modal/IdentityVerificationModal';
import _ from 'lodash';

interface IFormData {
  fullName: string;
  displayName: string;
  email: string;
  age: string;
  gender: string;
  phonePart1: string;
  phonePart2: string;
  phonePart3: string;
}

interface User {
  userId: string;
  fullName: string;
  displayName: string;
  email: string;
  gender: string;
  age: string;
  phoneNumber: string;
}

const SettingAccount: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifiedSuccess, setIsVerifiedSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm<IFormData>({
    mode: 'onSubmit',
    defaultValues: {
      fullName: '',
      displayName: '',
      email: '',
      age: '',
      gender: '',
      phonePart1: '',
      phonePart2: '',
      phonePart3: '',
    },
  });

  const phonePart1Ref = useRef<HTMLInputElement>(null);
  const phonePart2Ref = useRef<HTMLInputElement>(null);
  const phonePart3Ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = localStorage.getItem('user');
        const parsedUserData = userData ? JSON.parse(userData) : {};

        const response = await axios.get('/api/users/get', { params: parsedUserData });
        const data = response.data || parsedUserData;

        setUser(data);

        const { phoneNumber, ...rest } = data;
        let phonePart1 = '';
        let phonePart2 = '';
        let phonePart3 = '';
        if (phoneNumber) {
          [phonePart1, phonePart2, phonePart3] = phoneNumber.split('-');
        }

        reset({
          ...rest,
          phonePart1: phonePart1 || '',
          phonePart2: phonePart2 || '',
          phonePart3: phonePart3 || '',
        });
      } catch (error) {
        console.error(error);
      }
    };

    fetchUser();
  }, [reset]);

  const onSubmit: SubmitHandler<IFormData> = async data => {
    try {
      let temp = {
        userId: _.get(data, '_id'),
        fullName: _.get(data, 'fullName'),
        displayName: _.get(data, 'displayName'),
        email: _.get(data, 'email'),
        age: _.get(data, 'age'),
        gender: _.get(data, 'gender'),
      };
      const fullPhoneNumber = `${data.phonePart1}-${data.phonePart2}-${data.phonePart3}`;
      const response = await axios.post('/api/users/update', {
        ...temp,
        phoneNumber: fullPhoneNumber,
      });
      if (response.data) {
        alert('프로필이 업데이트되었습니다.');
        navigate('/profile');
      }
    } catch (error) {
      console.log(error);
      alert('프로필 업데이트에 실패했습니다.');
    }
  };

  const handlePhoneChange = (name: keyof IFormData, value: string) => {
    let filteredValue = value.replace(/[^0-9]/g, '');
    if (name === 'phonePart1') filteredValue = filteredValue.slice(0, 3);
    if (name === 'phonePart2') filteredValue = filteredValue.slice(0, 4);
    if (name === 'phonePart3') filteredValue = filteredValue.slice(0, 4);

    setValue(name, filteredValue, { shouldValidate: true, shouldDirty: true });

    if (name === 'phonePart1' && filteredValue.length === 3) phonePart2Ref.current?.focus();
    if (name === 'phonePart2' && filteredValue.length === 4) phonePart3Ref.current?.focus();
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, name: keyof IFormData) => {
    const { value, selectionStart } = e.currentTarget;
    if (e.key === 'Backspace' && selectionStart === 0 && value === '') {
      e.preventDefault();
      if (name === 'phonePart3') phonePart2Ref.current?.focus();
      if (name === 'phonePart2') phonePart1Ref.current?.focus();
    }
  };

  return (
    <div className="main-container">
      <div className="setting-container app-container">
        <div className="setting-section">
          <div className="section-header">
            <h2>프로필 수정</h2>
          </div>
          <div className="profile-info">
            <form onSubmit={handleSubmit(onSubmit)} className="edit-form">
              <div className="form-group">
                <label>아이디</label>
                <p className="form-value">{user?.userId}</p>
              </div>
              <div className="form-group">
                <label>이름</label>
                <Controller
                  name="fullName"
                  control={control}
                  rules={{ required: '이름은 필수 항목입니다.' }}
                  render={({ field }) => <input type="text" placeholder="이름" {...field} />}
                />
                {errors.fullName && <p className="error-message">{errors.fullName.message}</p>}
              </div>
              <div className="form-group">
                <label>닉네임</label>
                <Controller
                  name="displayName"
                  control={control}
                  rules={{ required: '닉네임은 필수 항목입니다.' }}
                  render={({ field }) => <input type="text" placeholder="닉네임" {...field} />}
                />
                {errors.displayName && (
                  <p className="error-message">{errors.displayName.message}</p>
                )}
              </div>
              <div className="form-group">
                <label>이메일</label>
                <Controller
                  name="email"
                  control={control}
                  rules={{ required: '이메일은 필수 항목입니다.' }}
                  render={({ field }) => <input type="email" placeholder="이메일" {...field} />}
                />
                {errors.email && <p className="error-message">{errors.email.message}</p>}
              </div>
              <div className="form-group">
                <label>나이</label>
                <Controller
                  name="age"
                  control={control}
                  rules={{ required: '나이는 필수 항목입니다.' }}
                  render={({ field }) => <input type="number" placeholder="나이" {...field} />}
                />
                {errors.age && <p className="error-message">{errors.age.message}</p>}
              </div>
              <div className="form-group">
                <label>성별</label>
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: '성별은 필수 항목입니다.' }}
                  render={({ field }) => (
                    <select {...field}>
                      <option value="male">남</option>
                      <option value="female">여</option>
                    </select>
                  )}
                />
                {errors.gender && <p className="error-message">{errors.gender.message}</p>}
              </div>
              <div className="form-group">
                <label>전화번호</label>
                <div className="phone-input-group">
                  <Controller
                    name="phonePart1"
                    control={control}
                    rules={{
                      required: '휴대폰 번호는 필수 항목입니다.',
                      pattern: {
                        value: /^[0-9]{3}$/,
                        message: '전화번호 형식이 올바르지 않습니다.',
                      },
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        maxLength={3}
                        ref={phonePart1Ref}
                        disabled={true}
                        onChange={e => {
                          field.onChange(e);
                          handlePhoneChange('phonePart1', e.target.value);
                        }}
                        onKeyDown={e => handlePhoneKeyDown(e, 'phonePart1')}
                      />
                    )}
                  />
                  <span>-</span>
                  <Controller
                    name="phonePart2"
                    control={control}
                    rules={{
                      required: '휴대폰 번호는 필수 항목입니다.',
                      pattern: {
                        value: /^[0-9]{3,4}$/,
                        message: '전화번호 형식이 올바르지 않습니다.',
                      },
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        maxLength={4}
                        ref={phonePart2Ref}
                        disabled={true}
                        onChange={e => {
                          field.onChange(e);
                          handlePhoneChange('phonePart2', e.target.value);
                        }}
                        onKeyDown={e => handlePhoneKeyDown(e, 'phonePart2')}
                      />
                    )}
                  />
                  <span>-</span>
                  <Controller
                    name="phonePart3"
                    control={control}
                    rules={{
                      required: '휴대폰 번호는 필수 항목입니다.',
                      pattern: {
                        value: /^[0-9]{4}$/,
                        message: '전화번호 형식이 올바르지 않습니다.',
                      },
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        maxLength={4}
                        ref={phonePart3Ref}
                        disabled={true}
                        onChange={e => {
                          field.onChange(e);
                          handlePhoneChange('phonePart3', e.target.value);
                        }}
                        onKeyDown={e => handlePhoneKeyDown(e, 'phonePart3')}
                      />
                    )}
                  />
                </div>
                {errors.phonePart1 && <p className="error-message">{errors.phonePart1.message}</p>}
                {errors.phonePart2 && <p className="error-message">{errors.phonePart2.message}</p>}
                {errors.phonePart3 && <p className="error-message">{errors.phonePart3.message}</p>}
                {/* {<button type="button" className="form-btn verify-btn" onClick={() => setIsVerified(true)}>인증하기</button>} */}
                {isVerifiedSuccess && <div>인증완료</div>}
              </div>
              <div className="form-btn-group">
                <button
                  type="button"
                  className="form-btn cancel-btn"
                  onClick={() => navigate('/setting')}
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="form-btn save-btn"
                  onClick={() => setIsVerified(false)}
                >
                  저장
                </button>
              </div>
            </form>
          </div>
        </div>
        {isVerified && (
          <IdentityVerificationModal isOpen={isVerified} onClose={() => setIsVerified(false)} />
        )}
      </div>
    </div>
  );
};

export default SettingAccount;
